import React from "react";
import { Box, Typography } from "@material-ui/core";
import { Save } from "@material-ui/icons";

import {
  RegistrationForm,
  ReviewForm,
  ReviewByAdminForm,
  CouncilForm,
  CouncilReviewForm,
  ProgressReportForm,
  FinalReportForm,
  RequestStopForm,
  RequestExtensionForm,
  DeleteForm,
} from "../InstitutionalProjectForms/v2";
import SettingForm from "../InstitutionalProjectForms/SettingForm";
import ExportForm  from "../InstitutionalProjectForms/ExportForm";
import InternalResearchHistory from "./InternalResearchHistory";
import {
  INT_STATUS_REGISTRATION_DRAFT,
  INT_STATUS_REGISTRATION_SUBMITTED,
  INT_STATUS_MOD_ADJUST_REQUEST,
  INT_STATUS_MOD_REJECTED,
  INT_STATUS_MOD_ACCEPTED,
  INT_STATUS_ADMIN_ADJUST_REQUEST,
  INT_STATUS_ADMIN_REJECTED,
  INT_STATUS_ADMIN_ACCEPTED,
  INT_STATUS_SELECTION_COUNCIL_PENDING,
  INT_STATUS_SELECTION_COUNCIL_ADJUST_REQUEST,
  INT_STATUS_SELECTION_COUNCIL_REJECTED,
  INT_STATUS_SELECTION_COUNCIL_ACCEPTED,
  INT_STATUS_CONDUCTED,
  INT_STATUS_CONDUCTED_PHASE2,
  INT_STATUS_MIDTERM_REPORT_SUBMITTED,
  INT_STATUS_MIDTERM_COUNCIL_PENDING,
  INT_STATUS_MIDTERM_COUNCIL_ADJUST_REQUEST,
  INT_STATUS_MIDTERM_COUNCIL_REJECTED,
  INT_STATUS_MIDTERM_COUNCIL_ACCEPTED,
  INT_STATUS_FINAL_REPORT_SUBMITTED,
  INT_STATUS_FINAL_COUNCIL_PENDING,
  INT_STATUS_FINAL_COUNCIL_ADJUST_REQUEST,
  INT_STATUS_FINAL_COUNCIL_REJECTED,
  INT_STATUS_FINAL_COUNCIL_ACCEPTED,
  INT_STATUS_STOP_REQUESTED,
  INT_STATUS_STOP_ACCEPTED,
  INT_STATUS_STOP_REJECTED,
  INT_STATUS_EXTENSION_REQUESTED,
  INT_STATUS_EXTENSION_ADJUST_REQUEST,
  INT_STATUS_EXTENSION_ACCEPTED,
  INT_STATUS_EXTENSION_REJECTED,
  INT_COUNCIL_TYPE_SELECTION,
  INT_COUNCIL_TYPE_MIDTERM,
  INT_COUNCIL_TYPE_FINAL,
  INT_DURATION_ONE_YEAR,
  INT_DURATION_TWO_YEAR,
  INTERNAL_RESEARCH_STATUS_LABEL,
} from "../../../constants/internalResearchStatus";

// ---------------------------------------------------------------------------
// Color mapping cho status chip (cùng tone với SR).
// ---------------------------------------------------------------------------
const color = {
  primary:   "#243474",
  success:   "#60ae7d",
  secondary: "#f46423",
  error:     "#e00000",
  neutral:   "#808080",
};

const COLOR_BY_STATUS = {
  [INT_STATUS_REGISTRATION_DRAFT]:               color.primary,
  [INT_STATUS_REGISTRATION_SUBMITTED]:           color.secondary,
  [INT_STATUS_MOD_ADJUST_REQUEST]:               color.secondary,
  [INT_STATUS_MOD_REJECTED]:                     color.error,
  [INT_STATUS_MOD_ACCEPTED]:                     color.success,
  [INT_STATUS_ADMIN_ADJUST_REQUEST]:             color.secondary,
  [INT_STATUS_ADMIN_REJECTED]:                   color.error,
  [INT_STATUS_ADMIN_ACCEPTED]:                   color.success,

  [INT_STATUS_SELECTION_COUNCIL_PENDING]:        color.secondary,
  [INT_STATUS_SELECTION_COUNCIL_ADJUST_REQUEST]: color.secondary,
  [INT_STATUS_SELECTION_COUNCIL_REJECTED]:       color.error,
  [INT_STATUS_SELECTION_COUNCIL_ACCEPTED]:       color.success,

  [INT_STATUS_CONDUCTED]:                        color.success,
  [INT_STATUS_CONDUCTED_PHASE2]:                 color.success,

  [INT_STATUS_MIDTERM_REPORT_SUBMITTED]:         color.secondary,
  [INT_STATUS_MIDTERM_COUNCIL_PENDING]:          color.secondary,
  [INT_STATUS_MIDTERM_COUNCIL_ADJUST_REQUEST]:   color.secondary,
  [INT_STATUS_MIDTERM_COUNCIL_REJECTED]:         color.error,
  [INT_STATUS_MIDTERM_COUNCIL_ACCEPTED]:         color.success,

  [INT_STATUS_FINAL_REPORT_SUBMITTED]:           color.secondary,
  [INT_STATUS_FINAL_COUNCIL_PENDING]:            color.secondary,
  [INT_STATUS_FINAL_COUNCIL_ADJUST_REQUEST]:     color.secondary,
  [INT_STATUS_FINAL_COUNCIL_REJECTED]:           color.error,
  [INT_STATUS_FINAL_COUNCIL_ACCEPTED]:           color.success,

  [INT_STATUS_STOP_REQUESTED]:                   color.secondary,
  [INT_STATUS_STOP_ACCEPTED]:                    color.neutral,
  [INT_STATUS_STOP_REJECTED]:                    color.error,

  [INT_STATUS_EXTENSION_REQUESTED]:              color.secondary,
  [INT_STATUS_EXTENSION_ADJUST_REQUEST]:         color.secondary,
  [INT_STATUS_EXTENSION_ACCEPTED]:               color.success,
  [INT_STATUS_EXTENSION_REJECTED]:               color.error,
};

// ---------------------------------------------------------------------------
// Modal properties — mỗi action key map → { title, size, form, actionButtons }.
// Hầu hết form tự có nút submit → actionButtons chỉ có closeButton.
// ---------------------------------------------------------------------------
const closeOnly = () => [{ closeButton: true }];

const modalPropertiesDict = {
  insert: {
    title: "Thêm đề tài Cơ sở",
    size: "lg",
    form: (p) => <RegistrationForm mode="create" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  edit: {
    title: "Chỉnh sửa đề tài",
    size: "lg",
    form: (p) => <RegistrationForm detail={p.detail} mode="edit" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  detail: {
    title: "Chi tiết đề tài",
    size: "lg",
    form: (p) => <RegistrationForm detail={p.detail} mode="view" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },

  // ----- Submit registration -----
  submitRegistration: {
    title: "Nộp đăng ký đề tài",
    size: "lg",
    form: (p) => <RegistrationForm detail={p.detail} mode="edit" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },

  // ----- Vòng duyệt đăng ký -----
  reviewByMod: {
    title: "Duyệt vòng đơn vị (Trưởng đơn vị)",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <RegistrationForm detail={p.detail} mode="view" />
        </Box>
        <ReviewForm
          title="Kết luận vòng đơn vị"
          onReview={async (action, comment) => {
            const ok = await p.dispatch(p.actions.reviewByMod(p.detail.id, action, comment));
            if (ok) p.handleClose();
            return ok;
          }}
        />
      </>
    ),
    actionButtons: closeOnly,
  },
  reviewByAdmin: {
    title: "Duyệt vòng Phòng QLKH",
    size: "lg",
    // Dùng `ReviewByAdminForm` (có ô Mã đề tài editable + suggest auto).
    // Form tự dispatch reviewByAdmin với researchCode → render.js không cần
    // truyền action callback như ReviewForm thường.
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <RegistrationForm detail={p.detail} mode="view" />
        </Box>
        <ReviewByAdminForm detail={p.detail} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ----- Hội đồng tuyển chọn (HĐ#1) -----
  selectionCouncil: {
    title: "Gán Hội đồng tuyển chọn (HĐ#1)",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <CouncilForm detail={p.detail} councilType={INT_COUNCIL_TYPE_SELECTION} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },
  reviewSelectionCouncil: {
    title: "Cập nhật kết quả HĐ tuyển chọn",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <CouncilReviewForm detail={p.detail} councilType={INT_COUNCIL_TYPE_SELECTION} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ----- BCTĐ (2y) -----
  progressReport: {
    title: "Báo cáo tiến độ (BCTĐ)",
    size: "md",
    form: (p) => <ProgressReportForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },

  // ----- Hội đồng tiến độ (HĐ#2) -----
  midtermCouncil: {
    title: "Gán Hội đồng đánh giá tiến độ (HĐ#2)",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <CouncilForm detail={p.detail} councilType={INT_COUNCIL_TYPE_MIDTERM} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },
  reviewMidtermCouncil: {
    title: "Cập nhật kết quả HĐ tiến độ",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <CouncilReviewForm detail={p.detail} councilType={INT_COUNCIL_TYPE_MIDTERM} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ----- Báo cáo tổng kết + HĐ#3 -----
  finalReport: {
    title: "Nộp báo cáo tổng kết",
    size: "md",
    form: (p) => <FinalReportForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  finalCouncil: {
    title: "Gán Hội đồng nghiệm thu (HĐ#3)",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <CouncilForm detail={p.detail} councilType={INT_COUNCIL_TYPE_FINAL} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },
  reviewFinalCouncil: {
    title: "Cập nhật kết quả HĐ nghiệm thu",
    size: "lg",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <CouncilReviewForm detail={p.detail} councilType={INT_COUNCIL_TYPE_FINAL} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ----- Xin dừng -----
  requestStop: {
    title: "Xin dừng thực hiện đề tài",
    size: "sm",
    form: (p) => <RequestStopForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  reviewStop: {
    title: "Duyệt đơn xin dừng",
    size: "md",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <ReviewForm
          title="Kết luận đơn xin dừng (Không duyệt = từ chối, CN tiếp tục thực hiện)"
          rejectLabel="Không duyệt"
          onReview={async (action, comment) => {
            const ok = await p.dispatch(p.actions.reviewStopInternal(p.detail.id, action, comment));
            if (ok) p.handleClose();
            return ok;
          }}
        />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ----- Gia hạn (1y only) -----
  requestExtension: {
    title: "Xin gia hạn đề tài (1 năm, +6 tháng)",
    size: "sm",
    form: (p) => <RequestExtensionForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  reviewExtension: {
    title: "Duyệt đơn xin gia hạn",
    size: "md",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <InternalResearchHistory detail={p.detail} />
        </Box>
        <ReviewForm
          title="Kết luận đơn xin gia hạn (Loại = từ chối)"
          onReview={async (action, comment) => {
            const ok = await p.dispatch(p.actions.reviewExtension(p.detail.id, action, comment));
            if (ok) p.handleClose();
            return ok;
          }}
        />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ----- Utilities -----
  delete: {
    title: "Xoá đề tài",
    size: "xs",
    form: (p) => <DeleteForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: () => [],
  },
  setting: {
    title: "Cấu hình hệ thống",
    size: "sm",
    form: (p) => <SettingForm handleClose={p.handleClose} year={p.year} id="setting-form" />,
    actionButtons: () => [
      { closeButton: true },
      {
        props: {
          form: "setting-form",
          type: "submit",
          color: "primary",
          variant: "contained",
          startIcon: <Save />,
        },
        content: "Lưu cấu hình",
      },
    ],
  },
  export: {
    title: "Xuất file báo cáo",
    size: "md",
    form: (p) => <ExportForm {...p} id="export-form" />,
    actionButtons: closeOnly,
  },
};

export const render = (open) => {
  if (!open) return null;
  for (const key of Object.keys(open)) {
    if (modalPropertiesDict[key]) return modalPropertiesDict[key];
  }
  return null;
};

// ---------------------------------------------------------------------------
// rowRender — map status hiện tại + type (role) → buttons.
// Roles:
//   - personal: CN đề tài
//   - mod:      TĐV (trưởng đơn vị)
//   - admin:    Phòng QLKH
//   - council:  Hội đồng (3 cấp — admin trực tiếp nhập kết quả)
// ---------------------------------------------------------------------------
const getButtonsFor = (status, type, row = {}) => {
  const isOneYear  = row?.durationMonths === INT_DURATION_ONE_YEAR;
  const isTwoYear  = row?.durationMonths === INT_DURATION_TWO_YEAR;
  const alreadyExtended = !!row?.extendedUntilDate;
  const canExtend  = isOneYear && !alreadyExtended;
  // Trigger HĐ#2 từ status `conducted` (refactor 2026-05-21):
  //   - 1y đã gia hạn → admin gán ngay (đánh giá pha gia hạn, không cần BCTĐ).
  //   - 2y → KHÔNG gán từ `conducted`; đợi CN nộp BCTĐ chuyển status sang
  //     `midterm_report_submitted` rồi mới hiện nút.
  //   - 1y chưa gia hạn → bỏ qua HĐ#2, CN nộp tổng kết thẳng.
  const needsMidtermFromConducted = isOneYear && alreadyExtended;
  // 1y chưa gia hạn được nộp tổng kết thẳng từ `conducted`.
  const canSubmitFinalFromConducted = isOneYear && !alreadyExtended;

  const rules = {
    // Refactor 2026-06-02: bỏ "delete" khỏi personal vì hạn chế chỉ sudo-admin
    // + admin Phòng QLKH (xem post-process appendDelete trong Table).
    [INT_STATUS_REGISTRATION_DRAFT]: {
      personal: ["edit", "submitRegistration"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_REGISTRATION_SUBMITTED]: {
      personal: ["detail"],
      mod:      ["reviewByMod"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_MOD_ADJUST_REQUEST]: {
      // Refactor 2026-05-21: bỏ nút `edit` thừa — `submitRegistration` mở cùng
      // form edit + có thêm hành động "Nộp đăng ký" (1 nút duy nhất, đủ nghĩa
      // nghiệp vụ "sửa + nộp lại").
      personal: ["submitRegistration"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_MOD_REJECTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
    [INT_STATUS_MOD_ACCEPTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewByAdmin"],
      council:  ["detail"],
    },
    [INT_STATUS_ADMIN_ADJUST_REQUEST]: {
      personal: ["submitRegistration"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_ADMIN_REJECTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
    [INT_STATUS_ADMIN_ACCEPTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["selectionCouncil"],
      council:  ["detail"],
    },

    [INT_STATUS_SELECTION_COUNCIL_PENDING]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewSelectionCouncil"],
      council:  ["detail"],
    },
    [INT_STATUS_SELECTION_COUNCIL_ADJUST_REQUEST]: {
      personal: ["submitRegistration"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_SELECTION_COUNCIL_REJECTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },

    [INT_STATUS_CONDUCTED]: {
      // Personal: 1y no-ext → finalReport thẳng; 2y → progressReport (BCTĐ);
      // 1y đã ext → đợi admin gán HĐ#2 (chỉ stop / requestStop được).
      personal: [
        ...(isTwoYear ? ["progressReport"] : []),
        ...(canSubmitFinalFromConducted ? ["finalReport"] : []),
        "requestStop",
        ...(canExtend ? ["requestExtension"] : []),
      ],
      mod:      ["detail"],
      // Admin chỉ gán HĐ#2 ở `conducted` khi 1y đã gia hạn.
      // 2y: đợi `midterm_report_submitted` (BCTĐ nộp xong) → status đó mới hiện nút.
      admin:    needsMidtermFromConducted ? ["midtermCouncil"] : ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_CONDUCTED_PHASE2]: {
      personal: [
        "finalReport",
        "requestStop",
        ...(canExtend ? ["requestExtension"] : []),
      ],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },

    [INT_STATUS_MIDTERM_REPORT_SUBMITTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["midtermCouncil"],
      council:  ["detail"],
    },
    [INT_STATUS_MIDTERM_COUNCIL_PENDING]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewMidtermCouncil"],
      council:  ["detail"],
    },
    [INT_STATUS_MIDTERM_COUNCIL_ADJUST_REQUEST]: {
      personal: ["progressReport"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_MIDTERM_COUNCIL_REJECTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },

    [INT_STATUS_FINAL_REPORT_SUBMITTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["finalCouncil"],
      council:  ["detail"],
    },
    [INT_STATUS_FINAL_COUNCIL_PENDING]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewFinalCouncil"],
      council:  ["detail"],
    },
    [INT_STATUS_FINAL_COUNCIL_ADJUST_REQUEST]: {
      personal: ["finalReport"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [INT_STATUS_FINAL_COUNCIL_REJECTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
    [INT_STATUS_FINAL_COUNCIL_ACCEPTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },

    [INT_STATUS_STOP_REQUESTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewStop"],
      council:  ["detail"],
    },
    [INT_STATUS_STOP_ACCEPTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
    [INT_STATUS_STOP_REJECTED]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },

    [INT_STATUS_EXTENSION_REQUESTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewExtension"],
      council:  ["detail"],
    },
    [INT_STATUS_EXTENSION_ADJUST_REQUEST]: {
      personal: ["requestExtension"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
  };

  const typeRow = rules[status];
  const buttons = typeRow?.[type] || ["detail"];
  return buttons.includes("detail") ? buttons : ["detail", ...buttons];
};

export const rowRender = (status, type, row = {}) => {
  let lastValue = "";
  if (Array.isArray(status) && status.length > 0) {
    const last = status[status.length - 1];
    lastValue = typeof last === "object" ? last?.value : last;
  } else if (typeof status === "string") {
    const arr = status.split(",");
    lastValue = arr[arr.length - 1];
  }

  const label = INTERNAL_RESEARCH_STATUS_LABEL[lastValue] || lastValue || "Không xác định";
  const bgcolor = COLOR_BY_STATUS[lastValue] || color.neutral;

  // "conference" role hiển thị giống "council".
  const normalisedType = type === "conference" ? "council" : type;

  const buttons = getButtonsFor(lastValue, normalisedType, row);
  return {
    status: [{ value: lastValue, label, bgcolor }],
    buttons,
  };
};
